import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';

/**
 * Decode CSV bytes to text. SheetJS reads a CSV without a BOM as Latin-1, which
 * turns Hebrew into mojibake ("דור" -> "×××"). Try UTF-8 (the default for most
 * exports, BOM or not), then Windows-1255 (Hebrew Excel's "CSV" save format).
 * @param {ArrayBuffer|Uint8Array} buffer
 * @returns {string}
 */
export function decodeCsv(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1255').decode(buffer);
  }
}

/**
 * Read the first sheet of an imported .xlsx / .xls / .csv file into row objects.
 * @param {File} file
 * @returns {Promise<object[]>}
 */
export async function readImportRows(file) {
  const buffer = await file.arrayBuffer();
  const wb = /\.csv$/i.test(file.name) || file.type === 'text/csv'
    ? xlsxRead(decodeCsv(buffer), { type: 'string' })
    : xlsxRead(buffer);
  return xlsxUtils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
}

/**
 * Turn one spreadsheet row into a registration request. Reads the columns the
 * Shooters page export writes (ID, Name, Division, Category, Power Factor, Club,
 * Email, Squad, extra fields by translated label or by key), case-insensitively.
 *
 * @param {object} row - a sheet_to_json row
 * @param {object} options
 * @param {object} options.match - the match (squads, registrationExtraFields, registrationLimitGSheet)
 * @param {object} options.extraFieldLabels - base extra field key -> column label
 * @returns {object|null} shooter without an id, or null for rows without a name
 */
export function rowToShooter(row, { match, extraFieldLabels = {} }) {
  const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/[\s_]+/g, '-');
  const cell = (...names) => {
    const wanted = names.filter(Boolean).map((n) => String(n).trim().toLowerCase());
    const key = Object.keys(row).find((k) => wanted.includes(k.trim().toLowerCase()));
    return key === undefined ? '' : String(row[key] ?? '').trim();
  };

  const name = cell('Name');
  if (!name) return null;

  const squadTitle = cell('Squad');
  const squad = squadTitle && (match?.squads || []).find((sq) => sq.title === squadTitle || sq.id === squadTitle);

  const extraFields = {};
  const extraKeys = (match?.registrationExtraFields || [])
    .filter((f) => typeof f === 'string' && !/^(equipment|image|covid)(_optional)?$/.test(f));
  for (const key of extraKeys) {
    const baseKey = key.replace(/_optional$/, '');
    const value = cell(key, baseKey, extraFieldLabels[baseKey]);
    if (value) extraFields[key] = value;
  }

  return {
    publicId: cell('ID', 'PublicId'),
    name,
    // With a federation list the server fills in the club and rejects one sent
    club: match?.registrationLimitGSheet ? null : (cell('Club') || null),
    email: cell('Email') || null,
    division: normalize(cell('Division')) || 'standard',
    category: normalize(cell('Category')) || 'regular',
    'power-factor': normalize(cell('Power Factor', 'power-factor')) || 'minor',
    ...(squad && { squad: squad.id }),
    ...(Object.keys(extraFields).length > 0 && { extraFields }),
  };
}
